import { Metamodel } from 'app/model/base/model';
import { compressionOptionValues } from 'app/shared/components/compression-setting/compression-options';

import { compressionCodecType, DelimitedTextDataset, fileFormatConstants, IValidationContext, IViolation } from 'shell-models-lib';
import { BooleanDataFlowType, StringDataFlowType } from '../dataflow-datatype';
import { DataFlowSink } from '../dataflow-sink';
import { DataFlowSource } from '../dataflow-source';
import { DataFlowExpression } from '../dataflowexpression';
import { DatasetFormatType } from './dataflow-dataset-settings';
import { validateFilePathOfFileBasedSettings } from './dataset-settings-validation-utils';
import { FileBasedSettings, FILEMODE } from './file-based-settings';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';

@Metamodel.model({
    name: adfModelTypeMap.delimitedText.dataflowSettings,
    validator: DelimitedTextSettings.validateDelimitedTextSettings
})
export class DelimitedTextSettings extends FileBasedSettings {
    public static datasetType = Metamodel.modelFor(DelimitedTextDataset).name;
    public compressionType: string;
    public compressionLevel: DataFlowExpression;
    public nullValue: DataFlowExpression;
    public rowDelimiter: DataFlowExpression;
    public escapeCharacter: DataFlowExpression;
    public quoteCharacter: DataFlowExpression;
    public encoding: DataFlowExpression;
    public columnDelimiter: DataFlowExpression;
    public columnNamesAsHeader: DataFlowExpression;

    public getDatasetType(): string {
        return Metamodel.modelFor(DelimitedTextDataset).name;
    }

    public getFormat(): DatasetFormatType {
        return DatasetFormatType.Delimited;
    }

    public getSubFormat(): string {
        return undefined;
    }

    public getIconClass(): string {
        return 'i-20x20-csv';
    }

    public initialize(subFormat: string): void {
        super.initialize(subFormat);
        this.columnNamesAsHeader = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
        this.compressionLevel = this.transform.createTemplatableExpression(StringDataFlowType.TYPE, compressionOptionValues.Fastest);
        this.nullValue = this.transform.createTemplatableExpression();
        this.rowDelimiter = this.transform.createTemplatableExpression();
        this.columnDelimiter = this.transform.createTemplatableExpression(StringDataFlowType.TYPE, ',');
        this.escapeCharacter = this.transform.createTemplatableExpression(StringDataFlowType.TYPE, fileFormatConstants.escapeCharDefaultForCsv);
        this.quoteCharacter = this.transform.createTemplatableExpression(StringDataFlowType.TYPE, fileFormatConstants.quoteCharDefaultForCsv);
        this.encoding = this.transform.createTemplatableExpression();
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public static async validateDelimitedTextSettings(settings: DelimitedTextSettings, context: IValidationContext): Promise<IViolation[]> {
        const violations: IViolation[] = [];
        if (settings.transform instanceof DataFlowSource) {
            const source = settings.transform;
            if (source.isInlineDataset && source.datasetSettings && source.datasetSettings.getFormat() === DatasetFormatType.Delimited) {
                violations.push(...(await validateFilePathOfFileBasedSettings(settings, false, context)));
                if (settings.fileMode === FILEMODE.FILE && settings.compressionType === compressionCodecType.gzip &&
                    !settings.fileName.templateExpression && !!settings.fileName.expression && !settings.fileName.expression.endsWith('.gz')) {
                    violations.push({
                        message: () => stringResources.Dataset_FileFormat_GZipFilenameError,
                        context: source
                    });
                }
            }
        }
        if (settings.transform instanceof DataFlowSink) {
            const sink = settings.transform;
            if (sink.isInlineDataset && sink.datasetSettings && sink.datasetSettings.getFormat() === DatasetFormatType.Delimited) {
                violations.push(...(await validateFilePathOfFileBasedSettings(settings, true, context)));
            }
        }

        if (settings.columnDelimiter.expression === '') {
            if (settings.encoding.expression && settings.encoding.expression !== 'UTF-8') {
                violations.push({
                    message: () => stringResources.Dataset_FileFormat_ColumnDelimiterShouldNotBeNoneError,
                    context: settings.transform
                });
            }
        }

        return violations.length > 0 ? violations : undefined;
    }
}
